import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Film, Users, BarChart3, Settings, Plus, Trash2, Edit, Eye,
  TrendingUp, Layers, Upload, X, Save, Search, Play, Tv,
  UserX, UserCheck, KeyRound, ChevronLeft, ChevronRight,
  Tag, Globe, Star, CreditCard, Check, Ticket, Calendar, Percent,
  Volume2, Joystick, ChevronUp, ChevronDown, RefreshCw, Sparkles, SlidersHorizontal, AlertTriangle, Scissors, GripVertical
} from 'lucide-react';
import { TrimEditor, fmtTrim } from './TrimEditor';
import { AiTrailerButton, AiTrailerBulkButton } from './AiTrailer';
import { ConfirmModal } from './TrashTab';
import FilterTimelineEditor from '../FilterTimelineEditor';
import { LanguageStats } from '../analytics/LanguageStats';
import {
  API, CATEGORIES_LIST, ALL_COUNTRIES, MEDIA_SPECS, LANGUAGES, TYPE_LABELS, TYPE_LABELS_PLURAL, uploadFileToS3, FilterBadge,
  formatFileSize, TimecodeInput, CountryPicker, FilterSelect, LoadingSpinner as SharedLoadingSpinner, validateMediaFile
} from './shared';
import { EpisodePreview, EpisodePreviewModal } from './EpisodePreview';
import { LoadingSpinner } from './core';

export function StripeSyncTab({ theme }) {
  const light = theme === 'light';
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCustomers, setShowCustomers] = useState(false);

  const cardCls = light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5';
  const hintCls = light ? 'text-gray-400' : 'text-white/30';

  const fetchData = async () => {
    try {
      const [statsRes, custRes] = await Promise.all([
        axios.get(`${API}/admin/stripe-stats`, { withCredentials: true }),
        axios.get(`${API}/admin/stripe-customers`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setCustomers(custRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const pollRef = useRef(null);

  const pollStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stripe-sync/status`, { withCredentials: true });
      if (data.syncing) {
        setSyncResult({ message: `Synchronisation en cours… ${data.processed || 0} clients traités`, errors: 0, inProgress: true });
      } else {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setSyncing(false);
        setSyncResult({ message: data.message || 'Synchronisation terminée', errors: data.error ? 1 : (data.errors || 0) });
        fetchData();
      }
    } catch { /* keep polling */ }
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const runSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/stripe-sync`, {}, { withCredentials: true });
      if (data.started || data.already_running) {
        setSyncResult({ message: data.already_running ? 'Une synchronisation est déjà en cours…' : 'Synchronisation démarrée…', errors: 0, inProgress: true });
        pollRef.current = setInterval(pollStatus, 2500);
      } else {
        setSyncResult(data);
        setSyncing(false);
      }
    } catch (e) {
      setSyncResult({ message: 'Erreur: ' + (e.response?.data?.detail || e.message), errors: 1 });
      setSyncing(false);
    }
  };

  if (loading) return <LoadingSpinner theme={theme} />;

  return (
    <div className="space-y-6" data-testid="stripe-sync-tab">
      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Synchronisation Stripe</h1>
        <button onClick={runSync} disabled={syncing} data-testid="sync-stripe-btn"
          className="flex items-center gap-2 bg-[#E9641A] hover:bg-[#FE975E] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors disabled:opacity-50">
          <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Synchronisation...' : 'Synchroniser maintenant'}
        </button>
      </div>

      {syncResult && (
        <div className={`p-4 rounded-xl border ${syncResult.inProgress ? 'bg-[#75D4D5]/10 border-[#75D4D5]/20 text-[#75D4D5]' : syncResult.errors > 0 ? 'bg-red-500/10 border-red-500/20 text-red-300' : 'bg-green-500/10 border-green-500/20 text-green-300'}`} data-testid="sync-result-banner">
          <p className="text-sm font-medium">{syncResult.message}</p>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Total synchronisés', value: stats.total_synced, color: 'text-white' },
            { label: 'Abonnements actifs', value: stats.active_subscriptions, color: 'text-green-400' },
            { label: 'Essai gratuit', value: stats.trialing, color: 'text-yellow-400' },
            { label: 'Inactifs', value: stats.inactive, color: 'text-white/40' },
            { label: 'Mot de passe à créer', value: stats.needs_password_reset, color: 'text-[#E9641A]' },
          ].map(s => (
            <div key={s.label} className={`p-4 rounded-xl border ${cardCls}`}>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className={`text-[10px] mt-1 ${hintCls}`}>{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className={`p-4 rounded-xl border ${light ? 'bg-blue-50 border-blue-100' : 'bg-[#75D4D5]/5 border-[#75D4D5]/10'}`}>
        <p className={`text-xs ${light ? 'text-blue-700' : 'text-[#75D4D5]'}`}>
          La synchronisation récupère tous les clients Stripe et leurs abonnements. Les utilisateurs migrés devront créer un mot de passe lors de leur première connexion sur la nouvelle plateforme.
        </p>
      </div>

      {/* Toggle customers list */}
      <button onClick={() => setShowCustomers(!showCustomers)}
        className={`text-sm font-semibold ${light ? 'text-[#E9641A]' : 'text-[#E9641A]'} hover:underline`}>
        {showCustomers ? 'Masquer' : 'Afficher'} les {customers.length} utilisateurs synchronisés
      </button>

      {showCustomers && (
        <div className={`rounded-xl border overflow-hidden ${cardCls}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-b ${light ? 'border-gray-100 bg-gray-50' : 'border-white/5 bg-white/[0.02]'}`}>
                <th className={`text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider ${hintCls}`}>Email</th>
                <th className={`text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider ${hintCls}`}>Nom</th>
                <th className={`text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider ${hintCls}`}>Abonnement</th>
                <th className={`text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider ${hintCls}`}>Statut</th>
                <th className={`text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider ${hintCls}`}>MDP</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${light ? 'divide-gray-50' : 'divide-white/5'}`}>
              {customers.map(c => (
                <tr key={c.email} className={`${light ? 'hover:bg-gray-50' : 'hover:bg-white/[0.02]'}`}>
                  <td className={`px-4 py-3 ${light ? 'text-gray-700' : 'text-white/80'}`}>{c.email}</td>
                  <td className={`px-4 py-3 ${light ? 'text-gray-500' : 'text-white/50'}`}>{c.name}</td>
                  <td className={`px-4 py-3 ${light ? 'text-gray-500' : 'text-white/50'} capitalize`}>{c.subscription_plan || 'free'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                      c.subscription_status === 'active' ? 'bg-green-500/15 text-green-400' :
                      c.subscription_status === 'trialing' ? 'bg-yellow-500/15 text-yellow-400' :
                      light ? 'bg-gray-100 text-gray-400' : 'bg-white/5 text-white/30'
                    }`}>{c.subscription_status || 'inactive'}</span>
                  </td>
                  <td className="px-4 py-3">
                    {c.needs_password_reset ? (
                      <span className="text-[10px] text-[#E9641A] font-semibold">À créer</span>
                    ) : (
                      <span className="text-[10px] text-green-400 font-semibold">OK</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


